import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
export let lastModified = undefined;
export let frontmatter = {
  "title": "Turborepo 1.9",
  "description": "Turborepo 1.9 focuses on improving observability for your task runs to better understand your caching behavior.",
  "date": "2023/04/11",
  "tag": "web development"
};
export let structuredData = {
  "contents": [{
    "heading": "",
    "content": "Turborepo 1.9 focuses on improving observability for your task runs to better understand your caching behavior:"
  }, {
    "heading": "",
    "content": "Run Summaries: Use the --summarize flag to generate a summary of your task to compare against previous runs."
  }, {
    "heading": "",
    "content": "Easier Starters: Use the --example flag with npx create-turbo to start from official Turborepo examples or custom repositories."
  }, {
    "heading": "",
    "content": "Strict Environments : Try enabling strict mode to restrict the environment variables your tasks have access to."
  }, {
    "heading": "",
    "content": "Update today by running npx @turbo/codemod migrate."
  }, {
    "heading": "view-and-compare-task-runs",
    "content": "You can now produce a JSON summary of your task run using the --summarize flag:"
  }, {
    "heading": "view-and-compare-task-runs",
    "content": "When this flag is enabled, Turborepo will generate a summary in .turbo/runs/ that contains all the information necessary to understand how turbo interpreted your your task's configuration and code."
  }, {
    "heading": "view-and-compare-task-runs",
    "content": "You can then compare summaries using your favorite JSON diffing tool to understand why you got a cache hit or a cache miss."
  }, {
    "heading": "view-and-compare-task-runs",
    "content": "Learn more in the docs."
  }, {
    "heading": "bring-your-own-starter",
    "content": "create-turbo now supports starting a new project from any of the official Turborepo examples. Get started with an example using a single command:"
  }, {
    "heading": "bring-your-own-starter",
    "content": "In your terminal UI, choose your preferred package manager and create-turbo will automatically convert the chosen example to your package manager of choice."
  }, {
    "heading": "bring-your-own-starter",
    "content": "Additionally, you can use create-turbo with custom repository sources, allowing you to re-use your own custom starter or another starter from around the community:"
  }, {
    "heading": "strict-environments-experimental",
    "content": "You can now use the --env-mode=strict flag to restrict the environment variables your tasks have access to. Your tasks will only be aware of the variables you explicitly state, creating a safer caching environment."
  }, {
    "heading": "strict-environments-experimental",
    "content": "In strict mode, Turborepo will pass environment variables declared in:"
  }, {
    "heading": "strict-environments-experimental",
    "content": "globalEnv and globalPassThroughEnv to all tasks"
  }, {
    "heading": "strict-environments-experimental",
    "content": "env and passThroughEnv for each task"
  }, {
    "heading": "strict-environments-experimental",
    "content": "In strict mode, this configuration will only expose four environment variables to your build tasks, helping you catch missing variables earlier in the development process."
  }, {
    "heading": "strict-environments-experimental",
    "content": "--env-mode also supports loose and infer."
  }, {
    "heading": "strict-environments-experimental",
    "content": "Learn more in the docs."
  }, {
    "heading": "community",
    "content": "Since releasing Turborepo v1.8 we've seen incredible adoption and community growth:"
  }, {
    "heading": "community",
    "content": "20.5k+ GitHub Stars"
  }, {
    "heading": "community",
    "content": "1.1M weekly NPM downloads"
  }, {
    "heading": "community",
    "content": "64 years of compute time saved through Remote Caching on Vercel"
  }, {
    "heading": "community",
    "content": "Turborepo is the result of the combined work of all of its contributors, including our core team."
  }, {
    "heading": "community",
    "content": "Thank you for your continued support, feedback, and collaboration to make Turborepo your build tool of choice."
  }],
  "headings": [{
    "id": "view-and-compare-task-runs",
    "content": "View and compare task runs"
  }, {
    "id": "bring-your-own-starter",
    "content": "Bring your own starter"
  }, {
    "id": "strict-environments-experimental",
    "content": "Strict Environments Experimental"
  }, {
    "id": "community",
    "content": "Community"
  }]
};
import {Authors} from '#components/authors';
import {ExperimentalBadge} from '#components/experimental-badge';
import {Date} from '#components/blog/date';
export const toc = [{
  depth: 2,
  url: "#view-and-compare-task-runs",
  title: _jsx(_Fragment, {
    children: "View and compare task runs"
  })
}, {
  depth: 2,
  url: "#bring-your-own-starter",
  title: _jsx(_Fragment, {
    children: "Bring your own starter"
  })
}, {
  depth: 2,
  url: "#strict-environments-experimental",
  title: _jsxs(_Fragment, {
    children: ["Strict Environments ", _jsx(ExperimentalBadge, {
      children: "Experimental"
    })]
  })
}, {
  depth: 2,
  url: "#community",
  title: _jsx(_Fragment, {
    children: "Community"
  })
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    h2: "h2",
    li: "li",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ul: "ul",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx("h1", {
      className: "text-center",
      children: "Turborepo 1.9"
    }), "\n", "\n", _jsx(Date, {
      children: "Monday, April 11th, 2023"
    }), "\n", _jsx(Authors, {
      authors: ['gregsoltis', 'nathanhammond', 'tomknickman', 'anthonyshew', 'jaredpalmer', 'mehulkar', 'chrisolszewski', 'nicholasyang', 'alexanderlyon']
    }), "\n", _jsx(_components.p, {
      children: "Turborepo 1.9 focuses on improving observability for your task runs to better understand your caching behavior:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "#view-and-compare-task-runs",
          children: _jsx(_components.strong, {
            children: "Run Summaries"
          })
        }), ": Use the ", _jsx(_components.code, {
          children: "--summarize"
        }), " flag to generate a summary of your task to compare against previous runs."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "#bring-your-own-starter",
          children: _jsx(_components.strong, {
            children: "Easier Starters"
          })
        }), ": Use the ", _jsx(_components.code, {
          children: "--example"
        }), " flag with ", _jsx(_components.code, {
          children: "npx create-turbo"
        }), " to start from official Turborepo examples or custom repositories."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsxs(_components.a, {
          href: "#strict-environments-experimental",
          children: [_jsx(_components.strong, {
            children: "Strict Environments"
          }), " ", _jsx(ExperimentalBadge, {
            isLink: false
          })]
        }), ": Try enabling strict mode to restrict the environment variables your tasks have access to."]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["Update today by running ", _jsx(_components.code, {
        children: "npx @turbo/codemod migrate"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "view-and-compare-task-runs",
      children: "View and compare task runs"
    }), "\n", _jsxs(_components.p, {
      children: ["You can now produce a JSON summary of your task run using the ", _jsx(_components.code, {
        children: "--summarize"
      }), " flag:"]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "Terminal",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"m 4,4 a 1,1 0 0 0 -0.7070312,0.2929687 1,1 0 0 0 0,1.4140625 L 8.5859375,11 3.2929688,16.292969 a 1,1 0 0 0 0,1.414062 1,1 0 0 0 1.4140624,0 l 5.9999998,-6 a 1.0001,1.0001 0 0 0 0,-1.414062 L 4.7070312,4.2929687 A 1,1 0 0 0 4,4 Z m 8,14 a 1,1 0 0 0 -1,1 1,1 0 0 0 1,1 h 8 a 1,1 0 0 0 1,-1 1,1 0 0 0 -1,-1 z\" fill=\"currentColor\" /></svg>",
        children: _jsx(_components.code, {
          children: _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-function)",
                "--shiki-dark": "var(--shiki-token-function)"
              },
              children: "turbo"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " build"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " --summarize"
            })]
          })
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["When this flag is enabled, Turborepo will generate a summary in ", _jsx(_components.code, {
        children: ".turbo/runs/"
      }), " that contains all the information necessary to understand how ", _jsx(_components.code, {
        children: "turbo"
      }), " interpreted your your task's configuration and code."]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "Terminal",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"m 4,4 a 1,1 0 0 0 -0.7070312,0.2929687 1,1 0 0 0 0,1.4140625 L 8.5859375,11 3.2929688,16.292969 a 1,1 0 0 0 0,1.414062 1,1 0 0 0 1.4140624,0 l 5.9999998,-6 a 1.0001,1.0001 0 0 0 0,-1.414062 L 4.7070312,4.2929687 A 1,1 0 0 0 4,4 Z m 8,14 a 1,1 0 0 0 -1,1 1,1 0 0 0 1,1 h 8 a 1,1 0 0 0 1,-1 1,1 0 0 0 -1,-1 z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-function)",
                "--shiki-dark": "var(--shiki-token-function)"
              },
              children: "Tasks:"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-constant)",
                "--shiki-dark": "var(--shiki-token-constant)"
              },
              children: "    3"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " successful,"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-constant)",
                "--shiki-dark": "var(--shiki-token-constant)"
              },
              children: " 3"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " total"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-function)",
                "--shiki-dark": "var(--shiki-token-function)"
              },
              children: "Cached:"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-constant)",
                "--shiki-dark": "var(--shiki-token-constant)"
              },
              children: "   0"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " cached,"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-constant)",
                "--shiki-dark": "var(--shiki-token-constant)"
              },
              children: " 3"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " total"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-function)",
                "--shiki-dark": "var(--shiki-token-function)"
              },
              children: "Time:"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: "     1.707s"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-function)",
                "--shiki-dark": "var(--shiki-token-function)"
              },
              children: "Summary:"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: "  /Users/acme/projects/acme/.turbo/runs/2Nn3X6nWDhP9ag8BnmivWRxHpHC.json"
            })]
          })]
        })
      })
    }), "\n", _jsx(_components.p, {
      children: "You can then compare summaries using your favorite JSON diffing tool to understand why you got a cache hit or a cache miss."
    }), "\n", _jsxs(_components.p, {
      children: ["Learn more in the ", _jsx(_components.a, {
        href: "/docs/reference/run#--summarize",
        children: "docs"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "bring-your-own-starter",
      children: "Bring your own starter"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "create-turbo"
      }), " now supports starting a new project from any of the official ", _jsx(_components.a, {
        href: "https://github.com/vercel/turbo/tree/main/examples",
        children: "Turborepo examples"
      }), ". Get started with an example using a single command:"]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "Terminal",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"m 4,4 a 1,1 0 0 0 -0.7070312,0.2929687 1,1 0 0 0 0,1.4140625 L 8.5859375,11 3.2929688,16.292969 a 1,1 0 0 0 0,1.414062 1,1 0 0 0 1.4140624,0 l 5.9999998,-6 a 1.0001,1.0001 0 0 0 0,-1.414062 L 4.7070312,4.2929687 A 1,1 0 0 0 4,4 Z m 8,14 a 1,1 0 0 0 -1,1 1,1 0 0 0 1,1 h 8 a 1,1 0 0 0 1,-1 1,1 0 0 0 -1,-1 z\" fill=\"currentColor\" /></svg>",
        children: _jsx(_components.code, {
          children: _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-function)",
                "--shiki-dark": "var(--shiki-token-function)"
              },
              children: "npx"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " create-turbo@latest"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " -e"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " kitchen-sink"
            })]
          })
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["In your terminal UI, choose your preferred package manager and ", _jsx(_components.code, {
        children: "create-turbo"
      }), " will automatically convert the chosen example to your package manager of choice."]
    }), "\n", _jsxs(_components.p, {
      children: ["Additionally, you can use ", _jsx(_components.code, {
        children: "create-turbo"
      }), " with custom repository sources, allowing you to re-use your own custom starter or another starter from around the community:"]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "Terminal",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"m 4,4 a 1,1 0 0 0 -0.7070312,0.2929687 1,1 0 0 0 0,1.4140625 L 8.5859375,11 3.2929688,16.292969 a 1,1 0 0 0 0,1.414062 1,1 0 0 0 1.4140624,0 l 5.9999998,-6 a 1.0001,1.0001 0 0 0 0,-1.414062 L 4.7070312,4.2929687 A 1,1 0 0 0 4,4 Z m 8,14 a 1,1 0 0 0 -1,1 1,1 0 0 0 1,1 h 8 a 1,1 0 0 0 1,-1 1,1 0 0 0 -1,-1 z\" fill=\"currentColor\" /></svg>",
        children: _jsx(_components.code, {
          children: _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-function)",
                "--shiki-dark": "var(--shiki-token-function)"
              },
              children: "npx"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " create-turbo"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " -e"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string)",
                "--shiki-dark": "var(--shiki-token-string)"
              },
              children: " https://github.com/your-org/turbo-starter"
            })]
          })
        })
      })
    }), "\n", _jsxs(_components.h2, {
      id: "strict-environments-experimental",
      children: ["Strict Environments ", _jsx(ExperimentalBadge, {
        children: "Experimental"
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["You can now use the ", _jsx(_components.code, {
        children: "--env-mode=strict"
      }), " flag to restrict the environment variables your tasks have access to. Your tasks will only be aware of the variables you explicitly state, creating a safer caching environment."]
    }), "\n", _jsxs(_components.p, {
      children: ["In ", _jsx(_components.code, {
        children: "strict"
      }), " mode, Turborepo will pass environment variables declared in:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.code, {
          children: "globalEnv"
        }), " and ", _jsx(_components.code, {
          children: "globalPassThroughEnv"
        }), " to all tasks"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.code, {
          children: "env"
        }), " and ", _jsx(_components.code, {
          children: "passThroughEnv"
        }), " for each task"]
      }), "\n"]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "./turbo.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-comment)",
                "--shiki-dark": "var(--shiki-token-comment)"
              },
              children: "  // Available to all tasks"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"globalPassThroughEnv\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\"GLOBAL_VAR_1\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "]"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: " "
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-comment)",
                "--shiki-dark": "var(--shiki-token-comment)"
              },
              children: "  // Available to all tasks and invalidates caches"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"globalEnv\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\"GLOBAL_VAR_2\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "]"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: " "
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"pipeline\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "    \"build\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-comment)",
                "--shiki-dark": "var(--shiki-token-comment)"
              },
              children: "      // Only available to `build` tasks"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "      \"passThroughEnv\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\"VAR_1\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "]"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: " "
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-comment)",
                "--shiki-dark": "var(--shiki-token-comment)"
              },
              children: "      // Available to `build` task and invalidates caches"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "      \"env\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\"VAR_2\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "]"
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "    }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["In ", _jsx(_components.code, {
        children: "strict"
      }), " mode, this configuration will only expose four environment variables to your ", _jsx(_components.code, {
        children: "build"
      }), " tasks, helping you catch missing variables earlier in the development process."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "--env-mode"
      }), " also supports ", _jsx(_components.code, {
        children: "loose"
      }), " and ", _jsx(_components.code, {
        children: "infer"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["Learn more in the ", _jsx(_components.a, {
        href: "/docs/reference/run#--env-mode-option",
        children: "docs"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "community",
      children: "Community"
    }), "\n", _jsxs(_components.p, {
      children: ["Since releasing ", _jsx(_components.a, {
        href: "/blog/turbo-1-8-0",
        children: "Turborepo v1.8"
      }), " we've seen incredible adoption and community growth:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "https://github.com/vercel/turbo",
          children: "20.5k+ GitHub Stars"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "https://www.npmjs.com/package/turbo",
          children: "1.1M weekly NPM downloads"
        })
      }), "\n", _jsxs(_components.li, {
        children: ["64 years of compute time saved through ", _jsx(_components.a, {
          href: "https://vercel.com/docs/concepts/monorepos/remote-caching",
          children: "Remote Caching on Vercel"
        })]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Turborepo is the result of the combined work of all of its contributors, including our core team."
    }), "\n", _jsx(_components.p, {
      children: "Thank you for your continued support, feedback, and collaboration to make Turborepo your build tool of choice."
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
